import { useEffect, useState } from "react";
import { getAssetKey, getAssetKind, getAssetPreviewUrl } from "./galleryUtils";

const RATIO_TARGETS = [
  { label: "horizontal", ratio: 1920 / 1080 },
  { label: "square", ratio: 1 },
  { label: "vertical", ratio: 1080 / 1920 },
];

const shapePromiseCache = new Map();
const shapeResultCache = new Map();

function loadImageShape(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const sourceRatio = image.naturalWidth / image.naturalHeight;
      const nearestTarget = RATIO_TARGETS.reduce((closest, target) => {
        const closestDelta = Math.abs(closest.ratio - sourceRatio);
        const targetDelta = Math.abs(target.ratio - sourceRatio);
        return targetDelta < closestDelta ? target : closest;
      }, RATIO_TARGETS[0]);

      resolve(nearestTarget.label);
    };

    image.onerror = () => reject(new Error(`Failed to load image shape: ${url}`));
    image.src = url;
  });
}

async function resolveAssetShape(asset) {
  const kind = getAssetKind(asset);
  if (kind === "pdf") {
    return "pdf";
  }

  const key = getAssetKey(asset, 0);
  if (shapeResultCache.has(key)) {
    return shapeResultCache.get(key);
  }

  const sourceUrl = getAssetPreviewUrl(asset) || asset?.url || "";
  if (!sourceUrl) {
    return "horizontal";
  }

  if (!shapePromiseCache.has(key)) {
    shapePromiseCache.set(
      key,
      loadImageShape(sourceUrl)
        .then((shape) => {
          shapeResultCache.set(key, shape);
          return shape;
        })
        .catch(() => {
          shapeResultCache.set(key, "horizontal");
          return "horizontal";
        })
    );
  }

  return shapePromiseCache.get(key);
}

function useGalleryAssetShapeMap(assets) {
  const [shapeMap, setShapeMap] = useState({});

  useEffect(() => {
    let active = true;

    Promise.all(
      assets.map(async (asset, index) => {
        const key = getAssetKey(asset, index);
        const shape = await resolveAssetShape(asset);
        return [key, shape];
      })
    ).then((entries) => {
      if (!active) {
        return;
      }

      setShapeMap(Object.fromEntries(entries));
    });

    return () => {
      active = false;
    };
  }, [assets]);

  return shapeMap;
}

export default useGalleryAssetShapeMap;
